import Image from 'next/image';
import { Dispatch, SetStateAction, useRef } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { useGetPresignedUrlMutation, useGetUserQuery } from '../../../api';
import { Button } from '../../../ui';

export const GrowthPlan = ({
  capitalRequestInput,
  handleChange,
  setCurrentStep,
  isLoading,
}: {
  capitalRequestInput: CapitalRequestInput;
  handleChange: (
    key: string,
    value: { fileName: string; s3Key: string }[]
  ) => void;
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  isLoading: boolean;
}) => {
  const { data: user } = useGetUserQuery();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const businessPlanInputRef = useRef<HTMLInputElement>(null);
  const strategicPlanInputRef = useRef<HTMLInputElement>(null);
  const actionPlanInputRef = useRef<HTMLInputElement>(null);
  const pitchDeckInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (
    type: 'business_plan' | 'strategic_plan' | 'action_plan' | 'pitch_deck',
    file: File
  ) => {
    const fileName = `${type}.${file.name.match(/\.([^\.]+)$/)?.[1] ?? ''}`;
    const presignedUrl = await getPresignedUrl({
      contentType: file.type,
      fileName,
    });
    if (presignedUrl.data?.presignedUrl) {
      await fetch(presignedUrl.data?.presignedUrl, {
        method: 'PUT',
        body: file,
      });
      handleChange('growthPlans', [
        ...capitalRequestInput.growthPlans,
        {
          fileName: fileName.split('/').join('_'),
          s3Key: `${user?.orgId}/${fileName}`,
        },
      ]);
    }
  };

  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold">Growth Plan</span>
          <span className="text-base font-normal">
            Please complete your Supplier Growth Plan on the platform to
            increase your chances of fulfilling this capital request.
          </span>
          <div className="w-fit">
            <Button variant="secondary" text="Complete Growth Plan" />
          </div>
          <span>
            If you prefer to continue without the Growth Plan, please select one
            of these documents to upload:
          </span>
          <div className="flex gap-2 items-center">
            <input
              type="checkbox"
              disabled
              checked={
                capitalRequestInput.growthPlans.findIndex((growthPlan) =>
                  growthPlan.fileName.includes('business_plan')
                ) != -1
              }
              className="peer size-4"
            />
            <div
              className="flex-grow rounded-2xl bg-gray-50 p-4 flex gap-2 cursor-pointer peer-checked:bg-primary-25 peer-checked:border peer-checked:border-primary-50"
              onClick={() => businessPlanInputRef.current?.click()}
            >
              <Image src={'/icons/upload.svg'} width={20} height={20} alt="" />
              <span>Business Plan</span>
              <input
                type="file"
                className="hidden"
                ref={businessPlanInputRef}
                onChange={(e) =>
                  e.target?.files?.[0] &&
                  uploadFile('business_plan', e.target.files[0])
                }
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="checkbox"
              disabled
              checked={
                capitalRequestInput.growthPlans.findIndex((growthPlan) =>
                  growthPlan.fileName.includes('strategic_plan')
                ) != -1
              }
              className="peer size-4"
            />
            <div
              className="flex-grow rounded-2xl bg-gray-50 p-4 flex gap-2 cursor-pointer peer-checked:bg-primary-25 peer-checked:border peer-checked:border-primary-50"
              onClick={() => strategicPlanInputRef.current?.click()}
            >
              <Image src={'/icons/upload.svg'} width={20} height={20} alt="" />
              <span>Strategic Plan</span>
              <input
                type="file"
                className="hidden"
                ref={strategicPlanInputRef}
                onChange={(e) =>
                  e.target?.files?.[0] &&
                  uploadFile('strategic_plan', e.target.files[0])
                }
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="checkbox"
              disabled
              checked={
                capitalRequestInput.growthPlans.findIndex((growthPlan) =>
                  growthPlan.fileName.includes('action_plan')
                ) != -1
              }
              className="peer size-4"
            />
            <div
              className="flex-grow rounded-2xl bg-gray-50 p-4 flex gap-2 cursor-pointer peer-checked:bg-primary-25 peer-checked:border peer-checked:border-primary-50"
              onClick={() => actionPlanInputRef.current?.click()}
            >
              <Image src={'/icons/upload.svg'} width={20} height={20} alt="" />
              <span>Action Plan</span>
              <input
                type="file"
                className="hidden"
                ref={actionPlanInputRef}
                onChange={(e) =>
                  e.target?.files?.[0] &&
                  uploadFile('action_plan', e.target.files[0])
                }
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="checkbox"
              disabled
              checked={
                capitalRequestInput.growthPlans.findIndex((growthPlan) =>
                  growthPlan.fileName.includes('pitch_deck')
                ) != -1
              }
              className="peer size-4"
            />
            <div
              className="flex-grow rounded-2xl bg-gray-50 p-4 flex gap-2 cursor-pointer peer-checked:bg-primary-25 peer-checked:border peer-checked:border-primary-50"
              onClick={() => pitchDeckInputRef.current?.click()}
            >
              <Image src={'/icons/upload.svg'} width={20} height={20} alt="" />
              <span>Pitch Deck</span>
              <input
                type="file"
                className="hidden"
                ref={pitchDeckInputRef}
                onChange={(e) =>
                  e.target?.files?.[0] &&
                  uploadFile('pitch_deck', e.target.files[0])
                }
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2"></div>
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('CAPITAL TYPES')}
            />
          </div>
          <div>
            <Button
              text="Continue"
              variant={isLoading ? 'disabled' : 'primary'}
              suffixIcon="/icons/arrow_right.svg"
              onClick={() => setCurrentStep('FINANCIALS')}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src="/create_cr.png"
          alt="Supplier Request Image"
          priority
          width={512}
          height={584}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
